import React, { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Typography,
} from "@material-ui/core";
import { getDoctorAppointmentWithoutOrgIdRequest } from "../../AppointmentApiActions";
import WeeklyScheduleGrid from "../WeeklyScheduleGrid/WeeklyScheduleGrid";
import SelectedDaySidebar from "../SelectedDaySidebar/SelectedDaySidebar";
import { useStyles } from "./styles";
import {
  getAllDeptsRequest,
  getAvailabilityRequest,
} from "../../../Departments/DepartmentsApiActions";
import { getPatientsWithoutParamsRequest } from "../../../Athletes/PatientApiActions";
import { getPatientCasesRequest } from "../../../Cases/CasesApiAction";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import BusinessIcon from "@material-ui/icons/Business";
import PersonIcon from "@material-ui/icons/Person";
import DescriptionIcon from "@material-ui/icons/Description";
import EventNoteIcon from "@material-ui/icons/EventNote";
import LinkIcon from "@material-ui/icons/Link";
import WifiRoundedIcon from "@material-ui/icons/WifiRounded";
import moment from "moment";
import { showSnackbar } from "../../../../utilities/UtilitiesActions";
import { APP_URLS, AthleteType, ROLES } from "../../../../utilities/constants";
import PatientCasesDialog from "../../../Cases/components/PatientCasesDialog/PatientCasesDialog";

const AppointmentsList = ({
  appointments,
  getAllAppointments,
  history,
  departmentData,
  patients,
  cases,
  getAllDepts,
  getAllPatient,
  getAllCases,
  user,
  availability,
  getDoctorAvailability,
}) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [openForm, setOpenForm] = useState(false);
  const [openCasesList, setOpenCasesList] = useState(false);
  const [patientCases, setPatientCases] = useState([]);

  const [viewMode, setViewMode] = useState("week");
  const [anchorDate, setAnchorDate] = useState(() => moment());
  const [selectedDate, setSelectedDate] = useState(() => moment());
  const [calMonth, setCalMonth] = useState(() => moment().startOf("month"));
  const [selectedEventId, setSelectedEventId] = useState(null);
  // Ticks while the details dialog is open so a slot that ends with the dialog
  // still open disables its meeting link there and then.
  const [now, setNow] = useState(() => Date.now());

  const [selectedAppointment, setSelectedAppointment] = useState({
    title: "",
    departmentName: "",
    patientName: "",
    caseName: "",
    appointmentDate: "",
    startTime: "",
    endTime: "",
    appointmentLink: "",
    patientPhone: "",
  });

  useEffect(() => {
    getAllAppointments();
  }, [getAllAppointments]);

  // Working hours decide how far the grid runs, so a 10:00-21:00 day is fully
  // visible instead of being cut off at the default range: the doctor's own
  // hours for a doctor, the clinic's hours for anyone else (receptionist).
  const workingHoursQuery =
    user?.Roles?.[0]?.name === ROLES.ROLE_DOCTOR && user?.id
      ? `userId=${user.id}`
      : user?.orgs?.[0]?.id
      ? `organizationId=${user.orgs[0].id}`
      : null;

  useEffect(() => {
    if (workingHoursQuery) {
      getDoctorAvailability(workingHoursQuery).catch(() => {});
    }
  }, [workingHoursQuery, getDoctorAvailability]);

  const handleEventClick = async (info) => {
    const selectedAppointment = appointments.find(
      (appointment) => appointment.id === parseInt(info.event.id)
    );
    if (!selectedAppointment) return;

    try {
      const patientsData = await getAllPatient(
        selectedAppointment.userId,
        AthleteType.USER
      );

      const casesData = await getAllCases(selectedAppointment.PatientId);
      const departmentData = await getAllDepts();

      const selectedPatientData =
        patientsData?.rows?.find(
          (patient) => patient.id === selectedAppointment.PatientId
        ) || null;
      const selectedCaseData =
        casesData?.rows?.find(
          (casee) => casee.PatientId === selectedAppointment.PatientId
        ) || null;
      const selectedDepartmentData =
        departmentData?.rows?.find(
          (dept) => dept.id === selectedAppointment.TeamId
        ) || null;

      if (!selectedPatientData || !selectedCaseData) {
        dispatch(
          showSnackbar(
            `${!selectedCaseData ? "Cases" : "Patient"} are not available`,
            "error"
          )
        );
        return;
      }

      const requestData = {
        appointmentId: selectedAppointment.id,
        title: selectedAppointment.title,
        departmentName: selectedDepartmentData.name,
        patientName: `${selectedPatientData.firstName} ${selectedPatientData.lastName}`,
        caseName: selectedCaseData.desc,
        patientPhone: selectedPatientData.mobile,
        appointmentDate: selectedAppointment.appointmentDate,
        startTime: selectedAppointment.startTime,
        endTime: selectedAppointment.endTime,
        appointmentLink: selectedAppointment.appointmentLink,
        caseId: selectedAppointment.CaseId,
        appointmentType: selectedAppointment.appointmentType,
      };

      setSelectedAppointment(requestData);
      setPatientCases(casesData?.rows || []);

      setOpenForm(true);
    } catch (error) {
      console.error("Error fetching patient or case data:", error);
    }
  };

  useEffect(() => {
    if (!openForm) return undefined;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, [openForm]);

  const formatTime = (timeInMillis) => {
    if (!timeInMillis) return "";
    const date = new Date(timeInMillis);
    return date.toTimeString().substring(0, 5);
  };

  // The doctor picks which of the patient's cases to open instead of jumping
  // straight to the case linked to the appointment.
  const handleViewCase = () => {
    setOpenForm(false);
    setOpenCasesList(true);
  };

  const handleCaseSelect = (caseItem) => {
    setOpenCasesList(false);
    history.push(`${APP_URLS.CASES}/${caseItem.id}`);
  };

  const allAppts = appointments || [];

  const openAppointment = (appt) => {
    setSelectedEventId(appt.id);
    handleEventClick({ event: { id: appt.id } });
  };

  const goPrev = () =>
    setAnchorDate((prev) =>
      prev.clone().subtract(1, viewMode === "week" ? "week" : "day")
    );
  const goNext = () =>
    setAnchorDate((prev) =>
      prev.clone().add(1, viewMode === "week" ? "week" : "day")
    );

  const onSelectDay = (day) => {
    setSelectedDate(day.clone());
    if (day.month() !== calMonth.month()) {
      setCalMonth(day.clone().startOf("month"));
    }
  };

  const detailOnline =
    (selectedAppointment.appointmentType || "").toLowerCase() === "online";

  // The meeting link is only good for the duration of the slot. startTime /
  // endTime are full timestamps (the slot time merged onto the appointment
  // date), so comparing endTime with now is enough.
  const meetingEnded =
    !!selectedAppointment.endTime && now > Number(selectedAppointment.endTime);

  const renderDetailRow = (icon, label, value, sub) => (
    <Box className={classes.detailRow}>
      <Box className={classes.detailIcon}>{icon}</Box>
      <Box className={classes.detailText}>
        <Typography className={classes.detailLabel}>{label}</Typography>
        <Typography className={classes.detailValue}>{value}</Typography>
        {sub ? (
          <Typography className={classes.detailSub}>{sub}</Typography>
        ) : null}
      </Box>
    </Box>
  );

  return (
    <Box className={classes.pageRoot}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8} lg={9}>
          <WeeklyScheduleGrid
            appointments={allAppts}
            workingHours={availability}
            viewMode={viewMode}
            anchorDate={anchorDate}
            onPrev={goPrev}
            onNext={goNext}
            onViewChange={setViewMode}
            onChipClick={openAppointment}
            onDayHeaderClick={onSelectDay}
            selectedEventId={selectedEventId}
          />
        </Grid>

        <Grid item xs={12} md={4} lg={3}>
          <SelectedDaySidebar
            appointments={allAppts}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            calMonth={calMonth}
            setCalMonth={setCalMonth}
            onCardClick={openAppointment}
          />
        </Grid>
      </Grid>

      <Dialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        maxWidth="xs"
        fullWidth
        classes={{ paper: classes.dialogPaper }}
      >
        <Box className={classes.dialogHeader}>
          <Box className={classes.dialogHeaderLeft}>
            <Box className={classes.dialogHeaderIcon}>
              <EventNoteIcon style={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Typography className={classes.dialogTitleText}>
                Appointment Details
              </Typography>
              {selectedAppointment.title && (
                <Typography className={classes.dialogSubtitle}>
                  {selectedAppointment.title}
                </Typography>
              )}
            </Box>
          </Box>

          <Box
            className={classes.dialogTypeChip}
            style={
              detailOnline
                ? { backgroundColor: "#e7f7ee", color: "#0a8f54" }
                : { backgroundColor: "#eef2ff", color: "#4f46e5" }
            }
          >
            {detailOnline ? (
              <WifiRoundedIcon style={{ fontSize: 16 }} />
            ) : (
              <BusinessIcon style={{ fontSize: 16 }} />
            )}
            {detailOnline ? "Online" : "Offline"}
          </Box>
        </Box>

        <DialogContent className={classes.dialogBody}>
          {renderDetailRow(
            <CalendarTodayIcon style={{ fontSize: 18 }} />,
            "Date",
            moment(selectedAppointment.appointmentDate).format("DD MMM YYYY")
          )}

          {renderDetailRow(
            <AccessTimeIcon style={{ fontSize: 18 }} />,
            "Time",
            `${formatTime(selectedAppointment.startTime)} - ${formatTime(
              selectedAppointment.endTime
            )}`
          )}

          {selectedAppointment.departmentName &&
            renderDetailRow(
              <BusinessIcon style={{ fontSize: 18 }} />,
              "Department",
              selectedAppointment.departmentName
            )}

          {selectedAppointment.patientName &&
            renderDetailRow(
              <PersonIcon style={{ fontSize: 18 }} />,
              "Patient",
              selectedAppointment.patientName,
              selectedAppointment.patientPhone
                ? `Phone: ${selectedAppointment.patientPhone}`
                : null
            )}

          {selectedAppointment.caseName &&
            renderDetailRow(
              <DescriptionIcon style={{ fontSize: 18 }} />,
              "Case",
              selectedAppointment.caseName
            )}

          {selectedAppointment.appointmentLink &&
            renderDetailRow(
              <LinkIcon style={{ fontSize: 18 }} />,
              "Meeting Link",
              // Past its end time the link is dropped, not just greyed out, so
              // the meeting cannot be joined from here any more.
              meetingEnded ? (
                <Button
                  disabled
                  size="small"
                  className={classes.joinMeetingBtn}
                >
                  Join Meeting
                </Button>
              ) : (
                <Button
                  href={selectedAppointment.appointmentLink}
                  target="_blank"
                  color="primary"
                  size="small"
                  className={classes.joinMeetingBtn}
                >
                  Join Meeting
                </Button>
              ),
              meetingEnded ? "This appointment has ended." : null
            )}
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => setOpenForm(false)} className={classes.cancelBtn}>
            Close
          </Button>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            onClick={handleViewCase}
            className={classes.viewCasesBtn}
          >
            View Cases
          </Button>
        </DialogActions>
      </Dialog>

      <PatientCasesDialog
        open={openCasesList}
        onClose={() => setOpenCasesList(false)}
        onSelect={handleCaseSelect}
        cases={patientCases}
        patientName={selectedAppointment.patientName}
        currentCaseId={selectedAppointment.caseId}
        currentLabel="This appointment"
      />
    </Box>
  );
};

const mapStateToProps = (state) => {
  return {
    appointments: state.appointments.appointments,
    departmentData: state.departments.depts,
    patients: state.athletes.athletes,
    cases: state.athletes.cases,
    user: state.auth.loggedInUser,
    availability: state.departments.availability,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getAllAppointments: (isHideLoader = false) => {
      return dispatch(
        getDoctorAppointmentWithoutOrgIdRequest(isHideLoader)
      ).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    getDoctorAvailability: (id) => {
      // Hidden loader: the grid renders fine on the default hours until the
      // working hours land.
      return dispatch(getAvailabilityRequest(id, true)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    getAllDepts: () => {
      return dispatch(getAllDeptsRequest()).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    getAllPatient: (id, type) => {
      return dispatch(getPatientsWithoutParamsRequest(id, type)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },

    getAllCases: (id) => {
      return dispatch(getPatientCasesRequest(id)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(AppointmentsList);
